import React, { useState } from "react"
import { capitalize } from "humanize-plus"
import classnames from "classnames"
import parse from "html-react-parser"
import { GraphError } from "javascripts/types"
import { activateWithToken } from "./api"

import ErrorIcon from "images/icons/icon-error-triangle.svg?raw"

import {
  trackServiceConnectionStarted,
  trackServiceConnectionCompleted,
  trackServiceConnectionFailed,
} from "app/utils/mixpanel_tracking"

import styles from "./activation.module.scss"

interface Props {
  service: {
    name: string
    module_name: string
  }
  apiTokenAuthHelpLink?: string
  liveChannelId?: string
  liveChannelFields?: {
    [key: string]: string
  }
  successUrl: string
}

const ActivateServiceWithToken = ({ service, apiTokenAuthHelpLink, liveChannelId, liveChannelFields, successUrl }: Props) => {
  const fieldName = "API token"
  const existingLiveChannelPresent = liveChannelId
  const isNewConnection = !existingLiveChannelPresent
  const connectionContext = "manual"
  const entrypoint = "service_settings"
  const authType = "token"

  const fieldValue = liveChannelFields ? liveChannelFields["masked_api_token"] : ""

  const [token, setToken] = useState<string>(fieldValue)
  const [error, setError] = useState<string>("")

  const handleTokenChange = (e: Event) => {
    const token = (e.target as HTMLInputElement).value
    setToken(token)

    if (token.length >= 6) {
      setError("")
    }
  }

  const handleTokenSubmit = async (e: Event) => {
    e.preventDefault()

    if (token.length < 6) {
      setError(`${fieldName} must be six characters or longer`)
      return
    }

    try {
      const startTime = Date.now()

      trackServiceConnectionStarted({
        serviceName: service.name,
        serviceModuleName: service.module_name,
        authType,
        connectionContext,
        isNewConnection,
        entrypoint,
      })

      const response = await activateWithToken(service.module_name, token, !!existingLiveChannelPresent)
      const dynamicChannelFields = response?.liveChannelCreate?.live_channel?.dynamic_channel_fields
      const createdLiveChannelId = response?.liveChannelCreate?.live_channel?.id || liveChannelId
      const maskedApiToken = JSON.parse(dynamicChannelFields || null)?.masked_api_token
      if (maskedApiToken) {
        trackServiceConnectionCompleted({
          serviceName: service.name,
          serviceModuleName: service.module_name,
          authType,
          connectionContext,
          isNewConnection,
          liveChannelId: createdLiveChannelId,
          timeToConnect: Date.now() - startTime,
          entrypoint,
        })

        window.location.assign(successUrl)
      } else {
        trackServiceConnectionFailed({
          serviceName: service.name,
          serviceModuleName: service.module_name,
          authType,
          connectionContext,
          isNewConnection,
          entrypoint,
          errorType: "token_validation_failed",
        })
        setError(`Error validating your ${fieldName}`)
      }
    } catch (e) {
      trackServiceConnectionFailed({
        serviceName: service.name,
        serviceModuleName: service.module_name,
        authType,
        connectionContext,
        isNewConnection,
        entrypoint,
        errorType: (e as GraphError).details || `token_activation_failed`,
      })
      console.error(e)
      setError((e as GraphError).details || `Error validating your ${fieldName}`)
    }
  }

  return (
    <div className={styles.activation}>
      <div className={styles["service-fields"]}>
        <h1>Add your {fieldName}</h1>
        <div className={styles["service-activation-form"]}>
          <form className="new_live_channel" id="new_live_channel" onSubmit={handleTokenSubmit}>
            <div className={styles["live-channel-fields"]}>
              <p>
                Copy and paste your {service.name} {fieldName} into the field below to connect the service.
                {apiTokenAuthHelpLink && (
                  <p>
                    Learn where to find your token <a href={apiTokenAuthHelpLink} target="_blank" rel="noopener noreferrer">
                      here
                    </a>.
                  </p>
                )}
              </p>
              <>
                <div
                  className={classnames(
                    styles["activation-field-entry"],
                    { [styles.error]: !!error }
                  )}
                >
                  <label htmlFor={fieldName}>{capitalize(fieldName)}</label>
                  <input
                    type="text"
                    autoFocus={true}
                    name={fieldName}
                    id={fieldName}
                    value={token}
                    onChange={handleTokenChange}
                    autoComplete="off"
                    data-cobrowse-redacted
                  />
                  {error && (
                    <div className={classnames(styles["after-message"], styles.submit)}>
                      <span>{parse(ErrorIcon)}</span>
                      {error}
                    </div>
                  )}
                </div>

                <div className={styles["send-pin"]}>
                  <button
                    className="button-secondary"
                    onClick={handleTokenSubmit}
                    disabled={!token.length || !!error}
                  >
                    {existingLiveChannelPresent ? "Update" : "Connect"}
                  </button>
                </div>
              </>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

export default ActivateServiceWithToken
